import React from 'react';
// AppFrame removed: route-level layout wraps pages
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { ArrowLeft, Plus, X, Trash2 } from "lucide-react";
import { CustomRecipePayload } from "../../types/customRecipe";

// View-only types (mirrored from container)
interface Ingredient {
  name: string;
  amount: number;
  unit: string;
  category?: string;
  optional?: boolean;
}

interface Step {
  id: number;
  title: string;
  description: string;
}

interface CreateCustomRecipePageViewProps {
  // Data
  formData: Partial<CustomRecipePayload>;
  currentStep: number;
  isSubmitting: boolean;
  errors: Record<string, string>;
  steps: Step[];
  currentStepData?: Step;
  isAuthenticated: boolean;
  
  // Computed values
  canProceed: boolean;
  canGoBack: boolean;
  isLastStep: boolean;
  
  // Constants/Options
  commonUnits: string[];
  commonCategories: string[];
  commonCuisines: string[];
  difficultyOptions: { value: string; label: string; }[];
  commonDietaryTags: string[];
  
  // Event handlers - Form data
  onUpdateFormData: (field: keyof CustomRecipePayload, value: any) => void;
  
  // Event handlers - Ingredients
  onAddIngredient: () => void;
  onUpdateIngredient: (index: number, field: keyof Ingredient, value: any) => void;
  onRemoveIngredient: (index: number) => void;
  
  // Event handlers - Instructions
  onAddInstruction: () => void;
  onUpdateInstruction: (index: number, text: string) => void;
  onRemoveInstruction: (index: number) => void;
  
  // Event handlers - Dietary tags
  onAddDietaryTag: (tag: string) => void;
  onRemoveDietaryTag: (tag: string) => void;
  
  // Event handlers - Navigation
  onNextStep: () => void;
  onPrevStep: () => void;
  onGoToStep: (step: number) => void;
  onGoBack: () => void;
  onGoToPlans: () => void;
  
  // Event handlers - Form actions
  onValidateForm: () => boolean;
  onSubmit: () => void;
}

/**
 * CreateCustomRecipePage View - Pure UI component with no hooks or data fetching
 * 
 * This handles only:
 * - UI rendering based on props
 * - User interactions (forwarded to container via callbacks)
 * - Conditional rendering based on state
 * - No business logic, no side effects, no data fetching
 */
export default function CreateCustomRecipePageView({
  // Data
  formData,
  currentStep,
  isSubmitting,
  errors,
  steps,
  currentStepData,
  isAuthenticated,
  
  // Computed values
  canProceed,
  canGoBack,
  isLastStep,
  
  // Constants/Options
  commonUnits,
  commonCategories,
  commonCuisines,
  difficultyOptions,
  commonDietaryTags,
  
  // Event handlers - Form data
  onUpdateFormData,
  
  // Event handlers - Ingredients
  onAddIngredient,
  onUpdateIngredient,
  onRemoveIngredient,
  
  // Event handlers - Instructions
  onAddInstruction,
  onUpdateInstruction,
  onRemoveInstruction,
  
  // Event handlers - Dietary tags
  onAddDietaryTag,
  onRemoveDietaryTag,
  
  // Event handlers - Navigation
  onNextStep,
  onPrevStep,
  onGoToStep,
  onGoBack,
  onGoToPlans,
  
  // Event handlers - Form actions
  onValidateForm,
  onSubmit,
}: CreateCustomRecipePageViewProps) {

  // Step 1: Basic Info Render
  const renderBasicInfo = () => (
    <div className="space-y-6">
      <div>
        <label className="mb-2 block text-sm font-medium text-dusk">
          Recipe Title *
        </label>
        <Input
          value={formData.title || ""}
          onChange={(e) => onUpdateFormData("title", e.target.value)}
          placeholder="e.g., Grandma's Chocolate Chip Cookies"
          className={errors.title ? "border-red-500" : ""}
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-500">{errors.title}</p>
        )}
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-dusk">
          Description
        </label>
        <Textarea
          value={formData.description || ""}
          onChange={(e) => onUpdateFormData("description", e.target.value)}
          placeholder="Brief description of your recipe..."
          rows={3}
          className={errors.description ? "border-red-500" : ""}
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-500">{errors.description}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="mb-2 block text-sm font-medium text-dusk">
            Servings *
          </label>
          <Input
            type="number"
            min="1"
            value={formData.servings || ""}
            onChange={(e) =>
              onUpdateFormData("servings", parseInt(e.target.value) || 1)
            }
            className={errors.servings ? "border-red-500" : ""}
          />
          {errors.servings && (
            <p className="mt-1 text-sm text-red-500">{errors.servings}</p>
          )}
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-dusk">
            Difficulty
          </label>
          <select
            value={formData.difficulty || "medium"}
            onChange={(e) => onUpdateFormData("difficulty", e.target.value)}
            className="h-10 w-full rounded-md border border-stone px-3 focus:border-warning focus:ring-warning"
          >
            {difficultyOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="mb-2 block text-sm font-medium text-dusk">
            Prep Time (minutes)
          </label>
          <Input
            type="number"
            min="0"
            value={formData.prepTime || ""}
            onChange={(e) =>
              onUpdateFormData("prepTime", parseInt(e.target.value) || 0)
            }
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-dusk">
            Cook Time (minutes)
          </label>
          <Input
            type="number"
            min="0"
            value={formData.cookTime || ""}
            onChange={(e) =>
              onUpdateFormData("cookTime", parseInt(e.target.value) || 0)
            }
          />
        </div>
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-dusk">
          Cuisine
        </label>
        <Input
          value={formData.cuisine || ""}
          onChange={(e) => onUpdateFormData("cuisine", e.target.value)}
          placeholder="e.g., Italian, Mexican, Asian..."
        />
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-dusk">
          Dietary Tags
        </label>
        <div className="mb-2 flex flex-wrap gap-2">
          {commonDietaryTags.map((tag) => (
            <Badge
              key={tag}
              variant={
                (formData.dietaryTags || []).includes(tag)
                  ? "default"
                  : "outline"
              }
              className="cursor-pointer"
              onClick={() =>
                (formData.dietaryTags || []).includes(tag)
                  ? onRemoveDietaryTag(tag)
                  : onAddDietaryTag(tag)
              }
            >
              {tag}
            </Badge>
          ))}
        </div>
        <Input
          placeholder="Add custom dietary tag..."
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              const tag = e.currentTarget.value.trim();
              if (tag && !(formData.dietaryTags || []).includes(tag)) {
                onAddDietaryTag(tag);
                e.currentTarget.value = "";
              }
            }
          }}
        />
      </div>
    </div>
  );

  // Step 2: Ingredients Render
  const renderIngredients = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-dusk">Ingredients</h3>
        <Button onClick={onAddIngredient} size="sm" variant="outline">
          <Plus className="mr-2 h-4 w-4" />
          Add Ingredient
        </Button>
      </div>

      {errors.ingredients && (
        <p className="text-sm text-red-500">{errors.ingredients}</p>
      )}

      <div className="space-y-3">
        {(formData.ingredients || []).map((ingredient, index) => (
          <Card key={index} className="p-4">
            <div className="mb-3 flex items-center gap-2">
              <span className="text-sm font-medium text-dusk">
                Ingredient {index + 1}
              </span>
              <Button
                onClick={() => onRemoveIngredient(index)}
                size="sm"
                variant="ghost"
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <Input
                  placeholder="Ingredient name"
                  value={ingredient.name}
                  onChange={(e) =>
                    onUpdateIngredient(index, "name", e.target.value)
                  }
                  className={
                    errors[`ingredient_${index}_name`] ? "border-red-500" : ""
                  }
                />
                {errors[`ingredient_${index}_name`] && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors[`ingredient_${index}_name`]}
                  </p>
                )}
              </div>

              <div>
                <Input
                  type="number"
                  min="0"
                  step="0.25"
                  placeholder="Amount"
                  value={ingredient.amount}
                  onChange={(e) =>
                    onUpdateIngredient(
                      index,
                      "amount",
                      parseFloat(e.target.value) || 0,
                    )
                  }
                  className={
                    errors[`ingredient_${index}_amount`] ? "border-red-500" : ""
                  }
                />
                {errors[`ingredient_${index}_amount`] && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors[`ingredient_${index}_amount`]}
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <select
                  value={ingredient.unit}
                  onChange={(e) =>
                    onUpdateIngredient(index, "unit", e.target.value)
                  }
                  className="h-10 flex-1 rounded-md border border-stone px-3 focus:border-warning focus:ring-warning"
                >
                  {commonUnits.map((unit) => (
                    <option key={unit} value={unit}>
                      {unit}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-3">
              <select
                value={ingredient.category || "Other"}
                onChange={(e) =>
                  onUpdateIngredient(index, "category", e.target.value)
                }
                className="h-10 w-full rounded-md border border-stone px-3 focus:border-warning focus:ring-warning"
              >
                {commonCategories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  // Step 3: Instructions Render
  const renderInstructions = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-dusk">Instructions</h3>
        <Button onClick={onAddInstruction} size="sm" variant="outline">
          <Plus className="mr-2 h-4 w-4" />
          Add Step
        </Button>
      </div>

      {errors.instructions && (
        <p className="text-sm text-red-500">{errors.instructions}</p>
      )}

      <div className="space-y-3">
        {(formData.instructions || []).map((instruction, index) => (
          <Card key={index} className="p-4">
            <div className="mb-3 flex items-center gap-2">
              <span className="text-sm font-medium text-dusk">
                Step {index + 1}
              </span>
              <Button
                onClick={() => onRemoveInstruction(index)}
                size="sm"
                variant="ghost"
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <Textarea
              placeholder={`Step ${index + 1} instructions...`}
              value={instruction}
              onChange={(e) => onUpdateInstruction(index, e.target.value)}
              rows={3}
              className={errors[`instruction_${index}`] ? "border-red-500" : ""}
            />
            {errors[`instruction_${index}`] && (
              <p className="mt-1 text-xs text-red-500">
                {errors[`instruction_${index}`]}
              </p>
            )}
          </Card>
        ))}
      </div>
    </div>
  );

  // Step 4: Review Render
  const renderReview = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="mb-2 text-2xl font-semibold text-dusk">
          {formData.title}
        </h2>
        {formData.description && (
          <p className="text-supporting">{formData.description}</p>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recipe Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-dusk">Servings:</span>{" "}
              {formData.servings}
            </div>
            <div>
              <span className="font-medium text-dusk">Difficulty:</span>{" "}
              {formData.difficulty}
            </div>
            <div>
              <span className="font-medium text-dusk">Prep Time:</span>{" "}
              {formData.prepTime} min
            </div>
            <div>
              <span className="font-medium text-dusk">Cook Time:</span>{" "}
              {formData.cookTime} min
            </div>
            {formData.cuisine && (
              <div>
                <span className="font-medium text-dusk">Cuisine:</span>{" "}
                {formData.cuisine}
              </div>
            )}
          </div>

          {formData.dietaryTags && formData.dietaryTags.length > 0 && (
            <div>
              <span className="font-medium text-dusk">Dietary Tags:</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.dietaryTags.map((tag) => (
                  <Badge key={tag} variant="secondary">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ingredients ({formData.ingredients?.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(formData.ingredients || []).map((ingredient, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span>
                  {ingredient.amount} {ingredient.unit} {ingredient.name}
                </span>
                {ingredient.category && (
                  <Badge variant="outline" className="text-xs">
                    {ingredient.category}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Instructions ({formData.instructions?.length} steps)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(formData.instructions || []).map((instruction, index) => (
              <div key={index} className="flex gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-coral text-xs font-medium text-white">
                  {index + 1}
                </span>
                <p className="text-sm">{instruction}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {formData.nutrition &&
        (formData.nutrition.calories! > 0 ||
          formData.nutrition.protein! > 0 ||
          formData.nutrition.carbs! > 0 ||
          formData.nutrition.fat! > 0) && (
          <Card>
            <CardHeader>
              <CardTitle>Nutrition per Serving</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
                {formData.nutrition.calories! > 0 && (
                  <div>
                    <span className="font-medium text-dusk">Calories:</span>{" "}
                    {formData.nutrition.calories}
                  </div>
                )}
                {formData.nutrition.protein! > 0 && (
                  <div>
                    <span className="font-medium text-dusk">Protein:</span>{" "}
                    {formData.nutrition.protein}g
                  </div>
                )}
                {formData.nutrition.carbs! > 0 && (
                  <div>
                    <span className="font-medium text-dusk">Carbs:</span>{" "}
                    {formData.nutrition.carbs}g
                  </div>
                )}
                {formData.nutrition.fat! > 0 && (
                  <div>
                    <span className="font-medium text-dusk">Fat:</span>{" "}
                    {formData.nutrition.fat}g
                  </div>
                )}
                {formData.nutrition.fiber! > 0 && (
                  <div>
                    <span className="font-medium text-dusk">Fiber:</span>{" "}
                    {formData.nutrition.fiber}g
                  </div>
                )}
                {formData.nutrition.sugar! > 0 && (
                  <div>
                    <span className="font-medium text-dusk">Sugar:</span>{" "}
                    {formData.nutrition.sugar}g
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
    </div>
  );

  // Step content mapping
  const getStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderBasicInfo();
      case 2:
        return renderIngredients();
      case 3:
        return renderInstructions();
      case 4:
        return renderReview();
      default:
        return renderBasicInfo();
    }
  };

  return (
    <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <Button
            onClick={onGoBack}
            variant="ghost"
            size="sm"
            className="text-supporting hover:text-dusk"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-dusk">
              Create Custom Recipe
            </h1>
            <p className="text-supporting">
              Add your own recipe to your meal plans
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-8 flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                  step.id === currentStep
                    ? "bg-coral text-white"
                    : step.id < currentStep
                      ? "bg-sage text-white"
                      : "text-supporting bg-stone"
                }`}
              >
                {step.id < currentStep ? "✓" : step.id}
              </div>
              <span
                className={`ml-2 text-sm ${
                  step.id === currentStep
                    ? "font-medium text-dusk"
                    : "text-supporting"
                }`}
              >
                {step.title}
              </span>
              {index < steps.length - 1 && (
                <div
                  className={`mx-4 h-0.5 w-12 ${
                    step.id < currentStep ? "bg-sage" : "bg-stone"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Form Content */}
        <div className="rounded-lg border border-stone bg-white p-6">
          {getStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="mt-6 flex justify-between">
          <Button
            onClick={onPrevStep}
            disabled={!canGoBack}
            variant="outline"
          >
            Previous
          </Button>

          <div className="flex gap-3">
            {isLastStep ? (
              <Button
                onClick={onSubmit}
                disabled={isSubmitting}
                className="bg-coral hover:bg-coral/90"
              >
                {isSubmitting ? "Creating Recipe..." : "Create Recipe"}
              </Button>
            ) : (
              <Button
                onClick={onNextStep}
                disabled={!canProceed}
                className="bg-coral hover:bg-coral/90"
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
  );
}
